import React, { useState } from 'react';
import './EditBusinessForm.css';

function EditBusinessForm({ business, categories, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    ...business
  });

  const [photoPreviews, setPhotoPreviews] = useState(business.photos || []);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxPhotos = 5;
    const currentCount = photoPreviews.length;

    files.forEach((file, index) => {
      if (currentCount + index < maxPhotos && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPhotoPreviews(prev => {
            const newPreviews = [...prev, event.target.result];
            setFormData(prevForm => ({
              ...prevForm,
              photos: newPreviews.slice(0, maxPhotos)
            }));
            return newPreviews.slice(0, maxPhotos);
          });
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removePhoto = (index) => {
    const newPhotos = photoPreviews.filter((_, i) => i !== index);
    setPhotoPreviews(newPhotos);
    setFormData(prev => ({
      ...prev,
      photos: newPhotos
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Business name is required';
    if (!formData.type.trim()) newErrors.type = 'Business type is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.hours.trim()) newErrors.hours = 'Hours are required';
    if (photoPreviews.length === 0) newErrors.photos = 'At least one photo is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        photos: photoPreviews
      });
    }
  };

  return (
    <div className="edit-business-overlay">
      <div className="edit-business-modal">
        <div className="modal-header">
          <h2>✏️ Edit Business</h2>
          <button className="close-button" onClick={onCancel}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-form">
          {/* Business Name */}
          <div className="form-group">
            <label htmlFor="name">Business Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter business name"
            />
            {errors.name && <span className="error">{errors.name}</span>}
          </div>

          {/* Category & Type */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="categoryName"
                value={formData.categoryName || ''}
                onChange={handleInputChange}
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="type">Business Type *</label>
              <input
                type="text"
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                placeholder="e.g., Retail, Service"
              />
              {errors.type && <span className="error">{errors.type}</span>}
            </div>
          </div>

          {/* Address */}
          <div className="form-group">
            <label htmlFor="address">Address *</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter complete address"
              rows="3"
            />
            {errors.address && <span className="error">{errors.address}</span>}
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              placeholder="Tell about your business..."
              rows="3"
            />
          </div>

          {/* Contact Details */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 XXXXX XXXXX"
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Hours & Website */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="hours">Operating Hours *</label>
              <input
                type="text"
                id="hours"
                name="hours"
                value={formData.hours}
                onChange={handleInputChange}
                placeholder="9:00 AM - 9:00 PM"
              />
              {errors.hours && <span className="error">{errors.hours}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website || ''}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Location Coordinates */}
          <div className="form-group">
            <label>Location Coordinates</label>
            <div className="form-row">
              <input
                type="number"
                name="latitude"
                value={formData.latitude || ''}
                onChange={handleInputChange}
                placeholder="Latitude"
                step="0.0001"
              />
              <input
                type="number"
                name="longitude"
                value={formData.longitude || ''}
                onChange={handleInputChange}
                placeholder="Longitude"
                step="0.0001"
              />
            </div>
          </div>

          {/* Photo Management */}
          <div className="form-group">
            <label htmlFor="photos">Photos (Max 5)</label>
            <div className="photo-upload-area">
              <input
                type="file"
                id="photos"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="photos" className="upload-button">
                📸 Add More Photos
              </label>
              <small>{photoPreviews.length} of 5 photos</small>
            </div>

            {photoPreviews.length > 0 && (
              <div className="photo-previews">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="photo-preview">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="remove-photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            {errors.photos && <span className="error">{errors.photos}</span>}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="submit" className="submit-button">
              ✓ Save Changes
            </button>
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBusinessForm;
